import {
  AfterContentInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  forwardRef,
  inject
} from '@angular/core';

import { NgxResizeObserverService } from 'ngx-resize-observer';
import {
  BehaviorSubject,
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  skip,
  switchMap,
  take,
  takeUntil
} from 'rxjs';

import {
  BreakpointService,
  DirectionService,
  ResizeService,
  RtlScrollAxisType,
  debounceTimeAfterFirst,
  getTouchPosition
} from '@hdms/shared/util-core';

import { ButtonSliderArrowComponent } from './button-slider-arrow.component';
import { ButtonSliderService } from './button-slider.service';

export enum Direction {
  LEFT = 'left',
  RIGHT = 'right'
}

const DEBOUNCE_TIME = 150;
const SHIFT_DELTA = 150;
const SWIPE_THRESHOLD = 10;
const BUTTON_SHOW_THRESHOLD = 1;

@Directive()
export abstract class ButtonSliderAbstractComponent implements OnInit, AfterContentInit, OnDestroy {
  readonly direction = Direction;

  showButtonLeft = false;
  showButtonRight = false;
  buttonsUpdated = false;

  scrollerWidth = 0;
  scrollerViewStart = 0;
  scrollerViewEnd = 0;
  scrollerContentWidth = 0;

  protected readonly breakpointService = inject(BreakpointService);
  protected readonly unsubscribe$ = new Subject<void>();
  protected readonly dirService = inject(DirectionService);
  protected sliderElements: HTMLElement[];

  private readonly shift$ = new BehaviorSubject<number>(0);
  private readonly scrollerContentChanged$ = new Subject<void>();

  private readonly buttonSliderService = inject(ButtonSliderService);
  private readonly resizeService = inject(ResizeService);
  private readonly resizeObserver = inject(NgxResizeObserverService);
  private readonly cdr = inject(ChangeDetectorRef);

  private scrollType: RtlScrollAxisType;
  private isRtlAndInverted = false;

  @ViewChild('scrollerContent', { static: true })
  protected readonly scrollerContent: ElementRef<HTMLElement>;

  protected scrollerElement: HTMLElement;

  @ViewChild('scroller', { static: true })
  private readonly scroller: ElementRef<HTMLElement>;

  @ViewChild(forwardRef(() => ButtonSliderArrowComponent), { read: ElementRef })
  private readonly sliderButton: ElementRef<HTMLElement>;

  set shift(s: number) {
    this.shift$.next(s);
  }

  get shift(): number {
    return this.shift$.getValue();
  }

  get leftBound(): number {
    if (this.dirService.isLtr || this.scrollType === RtlScrollAxisType.NORMAL) {
      return 0;
    } else if (this.scrollType === RtlScrollAxisType.NEGATED) {
      return this.scrollerWidth - this.scrollerContentWidth;
    } else {
      return this.scrollerContentWidth - this.scrollerWidth;
    }
  }

  get rightBound(): number {
    if (this.dirService.isLtr || this.scrollType === RtlScrollAxisType.NORMAL) {
      return this.scrollerContentWidth - this.scrollerWidth;
    } else {
      return 0;
    }
  }

  ngOnInit(): void {
    this.detectScrollType();
    this.detectInvertedRtl();

    this.scrollerElement = this.scroller.nativeElement;

    /**
     * Observe content of scroller because if this changes
     * without a page resize calculations of arrow buttons will be wrong
     **/
    this.resizeObserver.observe(
      this.scrollerContent.nativeElement,
      () => {
        this.scrollerContentChanged$.next();
      },
      'border-box'
    );

    // Listen to buttonSliderService change event
    this.buttonSliderService.change$.pipe(takeUntil(this.unsubscribe$)).subscribe(direction => this.slide(direction));

    // Listen to breakpoint change event
    this.breakpointService.isMobileView$
      .pipe(skip(1), takeUntil(this.unsubscribe$))
      .subscribe(_ => this.breakpointChange());

    fromEvent(this.scrollerElement, 'scroll')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => this.syncShiftWithScrollLeft());
  }

  ngAfterContentInit(): void {
    this.updateDimensions();
    this.syncShiftWithScrollLeft();

    // Listen to shift change event
    this.shift$.pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe(_ => this.updateButtons());

    // On resize update dimensions and recheck arrow visibility
    merge(this.resizeService.onResize$, this.scrollerContentChanged$)
      .pipe(debounceTimeAfterFirst(DEBOUNCE_TIME), takeUntil(this.unsubscribe$))
      .subscribe(this.onResize.bind(this));

    this.addSwipeSnapToListener();
  }

  ngOnDestroy(): void {
    this.resizeObserver.unobserve(this.scrollerContent.nativeElement);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Perform animated scroll to a particular position
   * Reset shift to 0 in case scrollWidth is equal to clientWidth
   * @param shift number
   * @param smooth boolean
   */
  slideTo(shift = 0, smooth = true): void {
    const { scrollWidth, clientWidth } = this.scrollerElement;
    this.shift = scrollWidth > clientWidth ? shift : 0;
    this.scrollerElement.scrollTo({
      left: shift,
      behavior: smooth ? 'smooth' : undefined
    });
  }

  protected breakpointChange(): void {
    // Move the tabs to the beginning when switching breakpoints
    this.resetShift();
    this.onResize();
  }

  /**
   * Update scroller dimensions
   */
  protected updateDimensions(): void {
    const { width, left } = this.scrollerElement.getBoundingClientRect();

    this.scrollerWidth = width;
    this.scrollerViewStart = left;
    this.scrollerViewEnd = left + width;
    this.scrollerContentWidth = this.scrollerContent.nativeElement.scrollWidth;
  }

  /**
   * Update buttons visibility
   */
  protected updateButtons(): void {
    const shift = this.shift;
    const previousShowButtonLeft = this.showButtonLeft;
    const previousShowButtonRight = this.showButtonRight;

    if (!this.isRtlAndInverted) {
      this.showButtonLeft = shift > Math.ceil(this.leftBound + BUTTON_SHOW_THRESHOLD);
      this.showButtonRight = shift < Math.floor(this.rightBound - BUTTON_SHOW_THRESHOLD);
      /* RtlScrollAxisType.INVERTED */
    } else {
      this.showButtonLeft = shift < Math.floor(this.leftBound - BUTTON_SHOW_THRESHOLD);
      this.showButtonRight = shift > Math.ceil(this.rightBound + BUTTON_SHOW_THRESHOLD);
    }

    /* Now that showButtonLeft or showButtonRight has changed, the changes need to be propagated */
    if (previousShowButtonLeft !== this.showButtonLeft || previousShowButtonRight !== this.showButtonRight) {
      this.cdr.detectChanges();
      this.buttonsUpdated = true;
    }
  }

  /**
   * Reset the shift
   */
  protected resetShift(): void {
    this.shift = this.dirService.isLtr ? this.leftBound : this.rightBound;
    this.scrollerElement.scrollLeft = this.shift;
  }

  /**
   * ScrollLeft is not always 0 on pageload (rtl) therefore we need to sync
   * it and check buttons after content has initialized
   * */
  protected syncShiftWithScrollLeft(): void {
    this.shift = this.scrollLeft;
  }

  protected get scrollLeft(): number {
    return this.scrollerElement.scrollLeft;
  }

  protected get sliderButtonWidth(): number {
    return this.sliderButton?.nativeElement.clientWidth || 0;
  }

  /**
   * Scroll by fixed delta
   * @param direction Direction
   * @param delta number
   */
  protected slide(direction: Direction, delta?: number): void {
    delta ??= this.calculateDeltaByElementPos(direction);
    this.slideTo(this.calculateShift(delta, direction));
  }

  protected alignActiveItem(item: HTMLElement): void {
    if (item) {
      const { left, right } = item.getBoundingClientRect();
      const { left: sliderLeft, right: sliderRight } = this.scrollerElement.getBoundingClientRect();
      const sliderPadding = this.sliderButtonWidth;

      if (left < sliderLeft) {
        this.slide(Direction.LEFT, sliderLeft - left + sliderPadding);
      } else if (right > sliderRight) {
        this.slide(Direction.RIGHT, right - sliderRight + sliderPadding);
      }
    }
  }

  private onResize(): void {
    this.updateDimensions();
    this.updateButtons();
  }

  /**
   * After swiping the slider, depending on the swipe direction snap to
   * left/right position of next element intersecting with slider boundaries.
   * As touchend can also end with a momentum scrolling, first listen to
   * subsequent scroll to finish, before doing the snap-to.
   * @private
   */
  private addSwipeSnapToListener(): void {
    fromEvent<TouchEvent>(this.scrollerElement, 'touchstart')
      .pipe(
        switchMap(startEvt =>
          fromEvent<TouchEvent>(this.scrollerElement, 'touchend').pipe(
            map(endEvt => getTouchPosition(startEvt).x - getTouchPosition(endEvt).x),
            filter(delta => Math.abs(delta) > SWIPE_THRESHOLD),
            map(delta => (delta < 0 ? Direction.LEFT : Direction.RIGHT))
          )
        ),
        switchMap(direction =>
          this.shift$.pipe(
            debounceTime(DEBOUNCE_TIME),
            take(1),
            map(_ => direction)
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(direction => this.slide(direction));
  }

  private detectScrollType(): void {
    this.scrollType = this.dirService.rtlScrollAxisType;
  }

  private detectInvertedRtl(): void {
    this.isRtlAndInverted = this.dirService.isRtl && this.scrollType === RtlScrollAxisType.INVERTED;
  }

  private calculateDeltaByElementPos(direction: Direction): number {
    if (!Array.isArray(this.sliderElements)) {
      return 0;
    }

    const isLeft = direction === Direction.LEFT;
    const reverse = this.dirService.isRtl ? !isLeft : isLeft;
    const sliderPos = Math.round(this.scrollerElement.getBoundingClientRect()[direction]);
    const sliderElements = reverse ? [...this.sliderElements].reverse() : this.sliderElements;
    const sliderPadding = this.sliderButtonWidth;

    const elementPos = sliderElements
      .map(el => Math.round(el.getBoundingClientRect()[direction]))
      .find(elPos => (isLeft ? elPos < sliderPos : elPos > sliderPos));

    return (isLeft ? sliderPos - elementPos : elementPos - sliderPos) + sliderPadding;
  }

  /**
   * As there are three different implementations on how browsers handle rtl and scrolling
   * we need to account for them:
   * Chrome = RtlScrollAxisType.NORMAL
   * Firefox / Safari = RtlScrollAxisType.NEGATED
   * IE / Old EDGE = RtlScrollAxisType.INVERTED
   */
  private calculateShift(delta: number, direction: Direction): number {
    const shiftDelta = delta || SHIFT_DELTA;
    const shift = this.shift;

    if (!this.isRtlAndInverted) {
      if (direction === Direction.LEFT) {
        return Math.max(shift - shiftDelta, this.leftBound);
      } else {
        return Math.min(shift + shiftDelta, this.rightBound);
      }
    } else {
      if (direction === Direction.LEFT) {
        return Math.min(shift + shiftDelta, this.leftBound);
      } else {
        return Math.max(shift - shiftDelta, this.rightBound);
      }
    }
  }
}
